/**
 * @module cart-page
 * @description Full cart page entry point — loaded on /cart template only.
 *
 * Uses Section Rendering API + morph for seamless quantity updates
 * without full-page reload. Listens to CART_EVENTS for cross-component sync.
 *
 * @see {@link @stardust/cart} — core cart state + Shopify API
 * @see {@link @stardust/section-renderer} — Section Rendering API wrapper
 */
import { morphSection, sectionRenderer } from "@stardust/section-renderer";
import { CART_EVENTS } from "@stardust/cart";
import { formatMoney } from "@stardust/utilities";

class CartPage {
  #isUpdating = false;
  #updateTimeout = null;
  #lastOwnUpdate = 0;
  /** @type {boolean} */
  #rehydratingFromHistory = false;
  /** @type {((e: PageTransitionEvent) => void) | null} */
  #onPageShow = null;

  constructor() {
    this.sectionEl = document.querySelector('[data-section-type="cart"]');
    if (!this.sectionEl) return;

    this.sectionId = this.sectionEl.dataset.sectionId;
    this.wrapper = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!this.wrapper) return;

    this.initSection();
    this.bindEvents();
    this.#bindHistoryRestoration();
  }

  initSection() {
    this.initAffirm();
  }

  initAffirm() {
    const wrapper = this.wrapper?.querySelector("[data-affirm-wrapper]");
    if (!wrapper) return;
    const p = wrapper.querySelector(".affirm-as-low-as");
    if (!p) return;

    if (p.textContent.trim() || window.affirm) {
      wrapper.hidden = false;
      return;
    }

    const observer = new MutationObserver(() => {
      if (p.textContent.trim()) {
        wrapper.hidden = false;
        observer.disconnect();
      }
    });
    observer.observe(p, { childList: true, characterData: true, subtree: true });
  }

  bindEvents() {
    // Handle explicit remove button clicks
    this.wrapper.addEventListener("click", (e) => {
      const removeBtn = e.target.closest("[data-item-remove]");
      if (removeBtn) {
        const cartItem = removeBtn.closest("[data-cart-item]");
        if (cartItem) this.removeItem(cartItem);
      }
    });

    // Handle remove-at-qty-1 from quantity spinner — intercepts before Cart class
    this.wrapper.addEventListener("cart:remove-item", (e) => {
      e.stopPropagation(); // Prevent Cart class (cart drawer) from also handling
      const cartItem = e.target.closest("[data-cart-item]");
      if (cartItem) this.removeItem(cartItem);
    });

    this.wrapper.addEventListener("change", (e) => {
      if (!e.target.matches("[data-quantity-spinner-input]")) return;
      const cartItem = e.target.closest("[data-cart-item]");
      if (!cartItem) return;

      clearTimeout(this.#updateTimeout);
      this.#updateTimeout = setTimeout(() => {
        const line = parseInt(cartItem.dataset.line, 10);
        const quantity = parseInt(e.target.value, 10);
        if (!isNaN(line) && !isNaN(quantity)) {
          this.updateQuantity(line, quantity, `Updated quantity to ${quantity}`);
        }
      }, 300);
    });

    document.addEventListener(CART_EVENTS.updated, (e) => {
      if (this.#isUpdating) return;
      if (Date.now() - this.#lastOwnUpdate < 2000) return;
      if (e.detail) {
        this.updateCartState(e.detail);
        this.updateDiscounts(e.detail.cart_level_discount_applications);
        this.updateShipping(e.detail.items_subtotal_price);
        this.updateFinancing(e.detail.total_price);
      }
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        this.#refreshSectionRef();
      });
    });

    this.wrapper.addEventListener("theme:section:morph", () => {
      this.#refreshSectionRef();
    });
  }

  /**
   * After Shop Pay (or other wallet) redirect, browser back/forward and bfcache
   * can leave `content_for_additional_checkout_buttons` iframes empty. Re-fetch
   * the cart section so Shopify can re-inject Amazon Pay, Shop Pay, etc.
   */
  #bindHistoryRestoration() {
    this.#onPageShow = (e) => {
      if (this.#rehydratingFromHistory) return;
      const nav = performance.getEntriesByType("navigation")[0];
      const isBackForward =
        nav && "type" in nav && /** @type {PerformanceNavigationTiming} */ (nav).type === "back_forward";
      if (!e.persisted && !isBackForward) return;
      void this.#refreshCartSectionAfterHistoryNavigation();
    };
    window.addEventListener("pageshow", this.#onPageShow);
  }

  /**
   * @returns {Promise<void>}
   */
  async #refreshCartSectionAfterHistoryNavigation() {
    if (!this.sectionId) return;
    this.#rehydratingFromHistory = true;
    try {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#refreshSectionRef();
    } catch (err) {
      console.warn("[CartPage] Failed to refresh cart section after back navigation:", err);
    } finally {
      this.#rehydratingFromHistory = false;
    }
  }

  #refreshSectionRef() {
    this.sectionEl = document.querySelector('[data-section-type="cart"]');
    if (!this.sectionEl) return;
    this.initSection();
  }

  async updateQuantity(line, quantity, announcement = "") {
    this.#isUpdating = true;
    this.setLoading(true);

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          line,
          quantity,
          sections: this.sectionId,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        console.error("Cart update failed:", response.status, response.statusText);
        return false;
      }

      const data = await response.json();

      if (data.errors) {
        console.error("Cart error:", data.errors);
        return false;
      }

      const sectionHTML = data.sections?.[this.sectionId];
      if (sectionHTML) {
        morphSection(this.sectionId, sectionHTML);
      }

      this.updateCartBadges(data.item_count);
      this.updateCartState(data);
      this.updateDiscounts(data.cart_level_discount_applications);
      this.updateShipping(data.items_subtotal_price);
      this.updateFinancing(data.total_price);
      if (announcement) this.announce(announcement);
      this.#lastOwnUpdate = Date.now();
      document.dispatchEvent(new CustomEvent(CART_EVENTS.updated, { detail: data }));
      return true;
    } catch (error) {
      console.error("Cart update failed:", error);
      return false;
    } finally {
      this.setLoading(false);
      this.#isUpdating = false;
    }
  }

  async removeItem(cartItem) {
    clearTimeout(this.#updateTimeout);
    const line = parseInt(cartItem.dataset.line, 10);
    if (isNaN(line)) return;

    cartItem.style.transition = "opacity 0.25s ease, height 0.3s 0.15s ease";
    cartItem.style.overflow = "hidden";
    cartItem.style.opacity = "0";

    const ok = await this.updateQuantity(line, 0, "Removed item from cart");
    if (!ok) {
      cartItem.style.transition = "";
      cartItem.style.overflow = "";
      cartItem.style.opacity = "";
    }
  }

  setLoading(isLoading) {
    if (!this.sectionEl) return;
    this.sectionEl.setAttribute("aria-busy", isLoading.toString());

    const table = this.wrapper?.querySelector("[data-cart-table]");
    if (table) table.style.opacity = isLoading ? "0.6" : "";
  }

  announce(message) {
    const el = this.wrapper?.querySelector("[data-cart-aria-live]");
    if (!el) return;
    el.textContent = "";
    requestAnimationFrame(() => {
      el.textContent = message;
    });
  }

  updateCartBadges(count) {
    for (const el of document.querySelectorAll("[data-cart-count]")) {
      el.textContent = count ?? 0;
    }
  }

  updateCartState(data) {
    if (!this.sectionEl) return;
    this.sectionEl.dataset.hasItems = (data.item_count > 0).toString();
    this.sectionEl.dataset.hasDiscounts = ((data.cart_level_discount_applications?.length ?? 0) > 0).toString();
  }

  updateDiscounts(discounts) {
    const el = this.sectionEl?.querySelector("[data-cart-discounts]");
    if (!el) return;

    if (!discounts?.length) {
      el.classList.add("hidden");
      el.parentElement?.classList.add("hidden");
      return;
    }

    el.classList.remove("hidden");
    el.parentElement?.classList.remove("hidden");
    el.innerHTML = "";

    for (const d of discounts) {
      const li = document.createElement("li");
      li.className =
        "relative flex items-center gap-1 before:absolute before:-left-2 before:top-1/2 before:-translate-y-1/2 before:content-['-']";

      const amount = document.createElement("span");
      amount.className = "leading-none";
      amount.textContent = formatMoney(Number(d.total_allocated_amount) || 0);

      li.append(amount);
      el.append(li);
    }
  }

  updateShipping(subtotalPrice) {
    const el = this.sectionEl?.querySelector("[data-cart-shipping]");
    if (!el) return;

    const raw = el.dataset.freeShippingThreshold;
    const threshold = raw ? Number(raw) : NaN;
    if (Number.isNaN(threshold)) {
      el.textContent = "TBD";
    } else {
      el.textContent = subtotalPrice >= threshold ? "FREE" : "TBD";
    }
  }

  updateFinancing(totalPrice) {
    const affirmEl = this.wrapper?.querySelector(".affirm-as-low-as");
    if (affirmEl) {
      affirmEl.dataset.amount = totalPrice;
      const wrapper = affirmEl.closest("[data-affirm-wrapper]");
      if (wrapper && window.affirm) wrapper.hidden = false;
      window.affirm?.ui?.refresh?.();
    }

    const afterpayEl = this.wrapper?.querySelector("square-placement");
    if (afterpayEl) {
      afterpayEl.dataset.amount = (totalPrice / 100).toFixed(2);
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new CartPage());
} else {
  new CartPage();
}
